import { useMemo } from "react";

import { Button } from "react-migration/components/Button";
import { DividerV2 } from "react-migration/components/DividerV2";
import { Icon } from "react-migration/components/Icon";
import clsx from "clsx";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import Feature from "src/js/stores/user/Feature";
import { getLicenseCode, LicenseTier } from "../getLicenseTier";

export const UpgradeOptionCard = ({
  disabled,
  variant,
  period,
  activeTier,
  onClick,
}: {
  variant: LicenseTier;
  onClick?: () => void;
  disabled?: boolean;
  period: "monthly" | "yearly";
  activeTier: number;
}) => {
  const { t } = useTranslation();
  const isUSUser = hasFeature(Feature.usAccess);

  const variantLicenseTier = getLicenseCode({ license: variant });

  const buttonText = useMemo(() => {
    if (variantLicenseTier === activeTier) {
      return "nav.upgrade_modal.card.button.current";
    }
    switch (variantLicenseTier) {
      case 5:
        return "nav.upgrade_modal.card.hometrack.button";
      case 4:
        return "nav.upgrade_modal.card.button.contact";
      default:
        return variantLicenseTier > activeTier
          ? "nav.upgrade_modal.card.button.upgrade"
          : "nav.upgrade_modal.card.button.downgrade";
    }
  }, [activeTier, variantLicenseTier]);

  const features = useMemo(() => {
    switch (variant) {
      case LicenseTier.FREE:
        return ["1", "2"];
      case LicenseTier.STARTER:
        return isUSUser ? [] : ["1", "2", "3"];
      case LicenseTier.PRO:
        return ["1", "2"];
      case LicenseTier.PREMIUM:
        return isUSUser ? ["1", "2"] : ["1", "2", "3"];
      case LicenseTier.HOMETRACK:
        return ["1", "2", "3"];
    }
  }, [isUSUser, variant]);

  return (
    <div
      data-test-id={`upgrade-card-${variant}`}
      className="flex flex-1 flex-col gap-2 p-4 rounded-lg border-2 relative overflow-hidden border-border-action hover:bg-background-button-secondary text-text-base-secondary"
    >
      <section data-testid="upgrade-card-title" className="flex justify-between items-center">
        <h1 className="font-bold text-base">{t(`nav.upgrade_modal.card.${variant}.title`)}</h1>
        {disabled && (
          <Icon icon="icon-lt-verified-badge-fill" size="lg" extraClasses="text-background-badge-green-accent" />
        )}
      </section>

      <section data-testid="upgrade-card-pricing">
        <div className="flex items-baseline gap-1">
          {!isUSUser && (variant === LicenseTier.PREMIUM || variant === LicenseTier.HOMETRACK) ? (
            <div className="flex items-center gap-1 h-8">
              <Icon icon="icon-lt-pantone-fill" size="lg" />
              <h2 className="font-normal text-base">{t(`nav.upgrade_modal.card.${variant}.price`)}</h2>
            </div>
          ) : (
            <h2 className="font-bold text-2xl">
              {period === "monthly"
                ? t(`nav.upgrade_modal.card.${variant}.price`)
                : t(`nav.upgrade_modal.card.${variant}.price_annual`)}
            </h2>
          )}
          <h3 className="font-normal text-base">{t(`nav.upgrade_modal.card.${variant}.frequency`)}</h3>
        </div>

        {isUSUser ? (
          <section className="font-normal text-xs h-20">{t(`nav.upgrade_modal.card.${variant}.description`)}</section>
        ) : (
          <div className="h-9">
            {period === "yearly" && (
              <div className="text-sm">{t(`nav.upgrade_modal.card.${variant}.price_annual_total`)}</div>
            )}
            <div className="text-text-base-secondary-a text-xs">
              {period === "monthly"
                ? t(`nav.upgrade_modal.card.${variant}.vat`)
                : t(`nav.upgrade_modal.card.${variant}.vat_annual`)}
            </div>
          </div>
        )}
      </section>

      <section data-testid="upgrade-card-actions" className="flex flex-col gap-4">
        <Button disabled={disabled || variantLicenseTier === activeTier} onClick={onClick}>
          {t(buttonText)}
        </Button>
        <DividerV2 />
      </section>

      <section data-testid="upgrade-card-features" className="flex flex-col gap-1.5">
        {isUSUser && (
          <p className="text-text-base-secondary-a font-normal text-xs m-0 h-5">
            {t(`nav.upgrade_modal.card.${variant}.features.description`)}
          </p>
        )}
        {features.map((feature) => (
          <div className={clsx("flex", "flex-row", "gap-2")} key={feature}>
            <Icon icon="icon-lt-check-line" size="md" extraClasses="text-icon-base-secondary-a" />
            <span className="text-text-base-secondaryfont-normal text-xs">
              {t(`nav.upgrade_modal.card.${variant}.features.${feature}`)}
            </span>
          </div>
        ))}
      </section>
    </div>
  );
};
